import React from 'react';
import {Link, useLocation} from "react-router-dom";
import Switch from 'react-switch';
import {Badge, Card, Collapse, Empty, Statistic} from 'antd';
import {useTalentTeam} from "../../providers/TalentTeamProvider";
import {RightOutlined} from '@ant-design/icons'
import TeamMemberTable from "./components/TeamMemberTable";
import LoadingBanner from "../../components/LoadingBanner";
import ResourceNotFound from "../../components/errors/ResourceNotFound";
import PageError from "../../components/errors/PageError";
import {useAuth} from "../../providers/AuthProvider";
import InfoTooltip from "../../components/InfoTooltip";
import {useAPI} from "../../config/API";
import TalentManagerFillTeamMemberModal from "./components/TalentManagerFillTeamMemberModal";


const TalentTeamManage = (props) => {

    const {auth} = useAuth();
    const {pathname} = useLocation();
    const {
        team,
        error,
        isLoading,
        refetch,
        updateTeamDetails,
        requestApprovalForTeamServices,
        requestHelpFillingDisciplines,
        addTalentResourceMember,
        isUpdating,
        resendInvite,
        cancelInvite,
        leaveTeam,
        deleteTeam,
    } = useTalentTeam();

    const {
        data: supplierResources,
        isInitialLoading: isSupplierUsersLoading,
    } = useAPI(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id],  {
        enabled: !!auth && !!auth.clientOrg?.()?.id && auth.canManageClientResourceTalent()
    });
    const isSupplier = auth.canManageClientResourceTalent();
    const isManaging = auth.canManageTalent() && pathname.includes('manager');
    const detailLinkPrefix = isManaging ? 'manager' : (auth.canManageClientResourceTalent() ? 'supplier' : 'talent')

    if (isLoading) {
        return (
            <div className="talent-portal-page flex-1 flex-grow">
                <LoadingBanner message="Loading Team"/>
            </div>
        );
    } else if (error) {
        return (
            <PageError exitLink={`/${detailLinkPrefix}/teams`} exitText="Back to my teams"/>
        )
    } else if (!team) {
        return <ResourceNotFound type="Team" exitLink={`/${detailLinkPrefix}/teams`} exitText="Back to my teams"/>;
    }

    const servicesReadyForApproval = team.servicesReadyForApproval();
    const servicesWaitingForApproval = team.servicesWaitingForApproval();
    const totalServicesAvailable = team.approved_talent_service_groups_count + servicesReadyForApproval?.length + servicesWaitingForApproval?.length
    const totalServicesUnapproved = team.talent_service_groups?.length - team.approved_talent_service_groups_count;
    const servicesNotReadyForApproval = team.servicesNotReadyForApproval();
    const totalServicesReadyOrPending = servicesReadyForApproval?.length + servicesWaitingForApproval?.length
    const rolesMissingForServices = team.rolesMissingForServices();

    const canManageRequestApproval = team.can_manage && totalServicesReadyOrPending > 0;
    const canManageRequestFill = team.can_manage && rolesMissingForServices?.length > 0;


    const serviceCategories = TB.groupBy(team.talent_service_groups, e => {
        return e.category.name
    })

    return (
        <div className="talent-team-manage p-3 pb-10 flex flex-col items-stretch max-w-6xl mx-auto w-full">

            <div className="flex flex-row items-center gap-2">
                <p>Team Control Center</p>
                {!team?.canManageTeamProfile() && (
                    <Link to={`/${detailLinkPrefix}/teams`} className="btn btn-link btn-link-info text-xs py-0 px-1.5">
                        Back to all teams
                    </Link>
                )}
            </div>

            <div className="pt-2 sm:flex justify-between items-center">
                <h3 className="font-bold">{team.name}</h3>

                <div className="flex flex-row flex-nowrap gap-2 justify-end pt-1">

                    {team.is_owner ? (
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={async () => deleteTeam()}
                            disabled={isUpdating}>
                            Delete Team
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={async () => leaveTeam()}
                            disabled={isUpdating}>
                            Leave Team
                        </button>
                    )}


                    {team.is_approved && (
                        <Link to={`/team-profiles/${team.id}`} target="_blank" className="btn btn-dark">
                            View Client-Facing Team Profile
                        </Link>
                    )}
                </div>
            </div>

            <div
                className="pt-4 pb-8 mb-8 border-b border-dashed border-[#979797] grid grid-cols-1 sm:grid-cols-2 justify-between gap-4">

                <div className="bg-white w-full flex flex-col">
                    <div className="p-2 bg-[#C2CFE0] text-center">
                        <p className="uppercase text-xs font-bold">Project Types Status</p>
                    </div>
                    <div className="h-full justify-center flex flex-col gap-4 items-stretch">
                        <div className="w-full py-4 bg-light-gray3 flex justify-center self-stretch">

                            <div className="flex flex-row flex-grow self-stretch gap-6 items-center justify-center">
                                <Card className="team-stats">
                                    <Statistic
                                        title={(
                                            <div>
                                                <p className="text-[inherit] font-bold">
                                                    Project Types <br /> Selected
                                                </p>
                                            </div>
                                        )}
                                        value={team.talent_service_groups?.length || 0}
                                        valueStyle={{fontWeight: 500, textAlign: 'center'}}
                                    />
                                </Card>

                                <Card className="team-stats">
                                    <Statistic
                                        title={(
                                            <div className="flex flex-row items-center">
                                                <p className="text-[inherit] font-bold">
                                                    Project Types Approved

                                                    {totalServicesUnapproved > 0 && (
                                                        <Badge
                                                            id="services-not-approved"
                                                            count={(
                                                                <i className="fas ml-1.5 text-danger fa-exclamation-circle text-sm"/>
                                                            )}
                                                        />
                                                    )}
                                                </p>

                                            </div>
                                        )}
                                        value={team.approved_talent_service_groups_count || 0}
                                    />
                                    {totalServicesUnapproved > 0 && (
                                        <InfoTooltip type="dark" placement="top" target="#services-not-approved">
                                            <p className="px-1.5 py-1 text-white text-center">
                                                Your team
                                                has {TB.pluralize("other project type", totalServicesUnapproved, true)} that
                                                are not approved.
                                            </p>
                                        </InfoTooltip>
                                    )}

                                </Card>

                            </div>
                        </div>


                        <div className="px-4 pb-4 justify-center flex flex-col gap-4 items-stretch">
                            {/* <div> */}
                                <div className="flex flex-row items-center gap-3 flex-wrap justify-center lg:justify-between">
                                    <div className={TB.mapClass("flex flex-row flex-grow items-center justify-center", {
                                        "lg:justify-start": team.can_manage
                                    })}>
                                        <div className="w-[24px] mt-[-2px]">
                                            {(servicesNotReadyForApproval?.length > 0 || servicesReadyForApproval?.length > 0) && (
                                                <>
                                                    {servicesReadyForApproval?.length > 0 && (
                                                        <Badge id="services-ready-for-approval" count={(
                                                            <i className="fas text-danger fa-exclamation-circle text-base"/>
                                                        )}/>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <p className="text-base font-bold whitespace-nowrap">
                                            {servicesReadyForApproval?.length || 0} Ready to Submit for Approval
                                        </p>


                                    </div>
                                    {team.can_manage && (auth.canManageTalent() ? (
                                        <div className="w-[160px] flex flex-col items-center">
                                            <button
                                                disabled={isUpdating || !servicesReadyForApproval?.length}
                                                onClick={() => requestApprovalForTeamServices(servicesReadyForApproval)}
                                                className="btn whitespace-nowrap text-sm btn-primary">
                                                Approve All
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-[160px] flex flex-col items-center">
                                            <button
                                                disabled={isUpdating || !servicesReadyForApproval?.length || servicesWaitingForApproval.length}
                                                onClick={() => requestApprovalForTeamServices(servicesReadyForApproval)}
                                                className="btn whitespace-nowrap text-sm btn-primary">
                                                {servicesWaitingForApproval?.length > 0 ? (
                                                    <i>Request Pending</i>
                                                ) : 'Request Approval'}
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {!!servicesWaitingForApproval.length && (
                                    <div className="flex flex-row items-center gap-3 flex-wrap justify-center lg:justify-between">

                                        <div className={TB.mapClass("flex flex-row flex-grow items-center justify-center", {
                                            "lg:justify-start": team.can_manage
                                        })}>
                                            {(servicesNotReadyForApproval?.length > 0 || servicesReadyForApproval?.length > 0 || servicesWaitingForApproval?.length > 0) && (
                                                <div className="w-[24px] mt-[-2px]">
                                                    {servicesWaitingForApproval?.length > 0 && (
                                                        <Badge id="services-waiting-for-approval" count={(
                                                            <i className="fas text-danger fa-exclamation-circle text-base"/>
                                                        )}/>
                                                    )}
                                                </div>
                                            )}
                                            <p className="text-base font-bold whitespace-nowrap">
                                                {servicesWaitingForApproval?.length || 0} Pending Wripple's Approval
                                            </p>


                                        </div>

                                        {servicesWaitingForApproval?.length && (
                                            <p>We're working on it!</p>
                                        )}
                                    </div>
                                )}


                            <div className="flex flex-row items-center gap-3 flex-wrap justify-center lg:justify-between">
                                <div className={TB.mapClass("flex flex-row flex-grow items-center justify-center", {
                                    "lg:justify-start": team.can_manage
                                })}>
                                    {(servicesNotReadyForApproval?.length > 0 || servicesReadyForApproval?.length > 0 || servicesWaitingForApproval?.length > 0) && (
                                        <div className="w-[24px] mt-[-2px]">
                                            {servicesNotReadyForApproval?.length > 0 && (
                                                <Badge id="services-not-ready-for-approval" count={(
                                                    <i className="fas text-danger fa-exclamation-circle text-base"/>
                                                )}/>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-base font-bold whitespace-nowrap">
                                        {servicesNotReadyForApproval?.length || 0} Are Invalid Due to Missing Roles
                                    </p>

                                </div>

                                {team.can_manage && (
                                    <div className="w-[160px] flex flex-col items-center">
                                        <button
                                            className="btn btn-link btn-link-primary whitespace-nowrap min-w-[100px]"
                                            disabled={isUpdating}
                                            onClick={() => {
                                                let roleSectionElem = document.getElementById("project-types-role-section")
                                                if (roleSectionElem) {
                                                    $('html, body').animate({
                                                        scrollTop: $(roleSectionElem)
                                                            .offset().top
                                                    }, 1000);
                                                }
                                            }}>
                                            <span className="text-sm">
                                                resolve missing roles
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>


                            {team.can_manage && (
                                <Link disabled={isUpdating}
                                      to="../project-types"
                                      className="btn btn-primary self-center">
                                    <i className="fas fa-pen mr-2 text-white"/> Edit Project Types
                                </Link>
                            )}
                        </div>

                    </div>
                    {servicesReadyForApproval?.length > 0 && (
                        <InfoTooltip type="dark" placement="top" target="services-ready-for-approval">
                            <p className="px-1.5 py-1 text-white text-center">
                                Your team
                                has {TB.pluralize("project type", servicesReadyForApproval?.length, true)} that
                                can
                                be approved.
                            </p>
                        </InfoTooltip>
                    )}
                    {servicesWaitingForApproval?.length > 0 && (
                        <InfoTooltip type="dark" placement="top" target="services-waiting-for-approval">
                            <p className="px-1.5 py-1 text-white text-center">
                                Your team has {TB.pluralize("project type", servicesWaitingForApproval?.length, true)} awaiting Wripple approval.
                            </p>
                        </InfoTooltip>
                    )}
                     {servicesNotReadyForApproval?.length > 0 && (
                        <InfoTooltip type="dark" placement="top" target="services-not-ready-for-approval">
                            <p className="px-1.5 py-1 text-white text-center">
                                Your team is missing required roles
                                for {TB.pluralize("project type", servicesNotReadyForApproval?.length, true)}.
                            </p>
                        </InfoTooltip>
                    )}
                </div>
                <div className="bg-white w-full flex flex-col">
                    <div className="p-2 bg-[#C2CFE0] text-center">
                        <p className="uppercase text-xs font-bold">Team Availability</p>
                    </div>
                    <div className="p-4 h-full justify-center flex flex-col gap-6 items-center">


                        <React.Fragment>
                            <i className={TB.mapClass("text-4xl fas fa-thumbs-up", {
                                "text-green-300": team.is_approved && team.is_available && team.approved_talent_service_groups_count > 0,
                                "text-gray-400": !team.is_approved || !team.is_available || team.approved_talent_service_groups_count === 0
                            })}/>

                            <div className="flex justify-center items-center gap-2">
                                <Switch
                                    height={21}
                                    width={42}
                                    disabled={!team.is_approved || team.approved_talent_service_groups_count === 0 || !!isUpdating}
                                    checked={team.is_approved && team.is_available && team.approved_talent_service_groups_count > 0}
                                    onChange={val => {
                                        if (!team.can_manage) {
                                            return TB.toast('Only the team admins can update team availability', {theme: 'danger'})
                                        }
                                        if (!team.is_approved) {
                                            return TB.toast('Team availability cannot be set until approved for project types', {theme: 'danger'})
                                        }

                                        return updateTeamDetails({is_available: !team.is_available})
                                    }}
                                    onColor={TB.config('color.light_purple')}
                                    offColor={TB.config('color.disabled')}
                                    uncheckedIcon={null}
                                    checkedIcon={null}
                                    activeBoxShadow={'0 0 0.5px 1px ' + TB.config('color.success-dark')}
                                    className="react-switch"
                                    id={`accepting-jobs-${team.id}`}
                                />

                                <label className="text-xs font-bold tracking-widest uppercase"
                                       htmlFor={`accepting-jobs-${team.id}`}>
                                    Accepting Jobs
                                </label>
                            </div>

                            <div className="text-center px-8">
                                {!team.approved_talent_service_groups_count && (
                                    <p className="text-lg">
                                        You must have at least <strong>1</strong> project type approved before accepting jobs for your team.
                                    </p>
                                )}

                                {(team.is_approved && team.is_available) && team.approved_talent_service_groups_count ? (
                                    <p className="text-lg">
                                        Yes, I want to receive job opportunities for the <strong>
                                        {TB.pluralize("approved project type", team.approved_talent_service_groups_count, true)}
                                        </strong>.
                                    </p>
                                ) : ''}

                                {(!team.is_approved || !team.is_available) && team.approved_talent_service_groups_count ? (
                                    <p className="text-lg">
                                        Team availability is off. Turn availability on to accept jobs for <strong>
                                        {TB.pluralize("approved project type", team.approved_talent_service_groups_count, true)}
                                        </strong>.
                                    </p>
                                ) : ''}

                            </div>
                        </React.Fragment>
                    </div>
                </div>
            </div>


            <div className="pb-8 mb-8 border-b border-dashed border-[#979797]">

                <div className="px-6 py-4 mb-4 bg-[#C2CFE0]">
                    <p className="font-bold">Team Members</p>
                </div>

                <TeamMemberTable hideAuth={isSupplier || isManaging}/>

            </div>

            <div id="project-types-role-section">
                <div className="px-6 py-2 mb-4 bg-[#C2CFE0] flex justify-between items-center">
                    <p className="font-bold">Project Types & Required Roles</p>

                    {team.talent_service_groups?.length > 0 && team.can_manage && (
                        <Link to="../project-types" className="btn btn-primary">
                            <i className="fas fa-pen mr-2 text-white"/> Edit Project Types
                        </Link>
                    )}
                </div>

                {team.talent_service_groups?.length > 0 ? TB.map(serviceCategories, (talent_service_groups, serviceCategory) => {
                    const defaultActive = TB.first(TB.keys(serviceCategories)) === serviceCategory ? TB.first(talent_service_groups)?.id || 1 : ["1"]
                    return (
                        <div key={serviceCategory} className="mb-6">
                            <div className="bg-white p-4 text-xs uppercase font-bold border-b border-bluegray">
                                {serviceCategory}
                            </div>
                            <Collapse defaultActiveKey={defaultActive} ghost expandIcon={({isActive}) => (
                                <div className="flex gap-2 items-center pl-4 w-16">
                                    <RightOutlined style={{fontSize: 16}} rotate={isActive ? 90 : undefined}/>
                                    {isActive ? <i className="fas fa-folder-open text-base"></i> :
                                        <i className="fas fa-folder text-base mr-0.5"></i>}
                                </div>
                            )}>

                                {talent_service_groups.map(service => {
                                    let statusLabel, iconClass = 'h-4 w-4 rounded-full ';
                                    if (service.is_approved) {
                                        statusLabel = 'Approved';
                                        iconClass += 'bg-success';
                                    } else if (service.approval_requested) {
                                        statusLabel = 'Approval Requested';
                                        iconClass += 'bg-[#4B80E7]';
                                    } else if (!service.has_missing_required_disciplines) {
                                        statusLabel = 'Ready for Approval';
                                        iconClass += 'bg-warning';
                                    } else if (service.has_missing_required_disciplines) {
                                        statusLabel = 'Missing Required Roles';
                                        iconClass += 'bg-danger';
                                    } else {
                                        statusLabel = 'Missing Roles';
                                        iconClass += 'bg-danger';
                                    }

                                    return (
                                        <Collapse.Panel
                                            key={service.id}
                                            header={(
                                                <div
                                                    className="p-4 flex justify-between items-center font-bold text-xs gap-5">
                                                    <div className="sm:flex w-full">
                                                        <p>{service.name}</p>
                                                        <p className="font-normal pt-1 sm:pt-0 sm:ml-auto sm:font-bold">{statusLabel}</p>
                                                    </div>
                                                    <div className={iconClass}/>
                                                </div>
                                            )}
                                        >
                                            {TB.map(TB.orderBy(service.disciplines || [], 'is_optional', 'false'), discipline => {
                                                const matchedMembers = team.membersForServiceDiscipline(discipline)

                                                const matchedResources = isSupplier && !matchedMembers?.length && !isSupplierUsersLoading && supplierResources?.length > 0 &&
                                                    TB.filter(supplierResources, resource => resource.is_active && resource.is_available && TB.find(resource.talent_disciplines || [], ['id', discipline.id]))
                                                return (
                                                    <div key={discipline.id} className="flex border-b border-bluegray">
                                                        <div className="w-16"/>
                                                        <div className="p-4 text-xs w-full sm:flex justify-between">
                                                            <p className="pb-2 sm:pb-0 space-x-2">
                                                                <span className="underline">{discipline.name}</span>
                                                                <span
                                                                    className="no-underline text-[#6A6D74]">{discipline.is_optional && '(optional)'}</span>
                                                            </p>

                                                            {matchedMembers?.length > 0 ? (
                                                                <div className="flex flex-row gap-1.5">
                                                                    <span>
                                                                        {TB.pluralize('Role', matchedMembers?.length)} Filled:
                                                                    </span>

                                                                    {TB.map(matchedMembers, member => (
                                                                        <span id={`mem-${member.id}-${discipline.id}`}
                                                                              key={`mem-${member.id}-${discipline.id}`}
                                                                              className={TB.mapClass("underline", {
                                                                                  "font-semibold underline": member.user.is_active && member.user.is_available,
                                                                                  "line-through text-danger": !member.user.is_active || !member.user.is_available
                                                                              })}>
                                                                            <span className="text-black">
                                                                                {member.user?.firstNamePlusInitial?.()}
                                                                            </span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (discipline.has_requested_fill ? (
                                                                <span className="text-info">
                                                                    <i className="fas fa-clock mr-1"/>
                                                                    <i>Help Requested</i>
                                                                </span>
                                                            ) : (!isSupplier ? (
                                                                    !discipline.is_optional ? (
                                                                        <button disabled={isUpdating} onClick={() => {
                                                                            requestHelpFillingDisciplines([discipline])
                                                                        }}>
                                                                            <i className="fas fa-user-plus mr-2"/>
                                                                            <span className="underline">Let Wripple Help</span>
                                                                        </button>
                                                                    ) : (
                                                                        <span>Role Filled: <span
                                                                            className="font-semibold underline">None</span>
                                                                        </span>
                                                                    )
                                                                ) : (
                                                                    matchedResources?.length > 0 ? (
                                                                        <span className="flex flex-row gap-2">
                                                                            <span>
                                                                                <i className="fas fa-user-plus mr-2"/> Available Matching Resources:
                                                                            </span>
                                                                            {TB.map(matchedResources, resource => (
                                                                                <button
                                                                                    key={`ser${service.id}-dis${discipline.id}-res${resource.id}`}
                                                                                    onClick={async () => {
                                                                                        let confirm = await TB.alert(
                                                                                            "warning", "Add Talent Member To Team?",
                                                                                            `${resource?.fullName()} can fill this project type's role. Would you like to add them to this team?`, [
                                                                                                {title: 'Yes, Continue'},
                                                                                                {title: 'Cancel'}
                                                                                            ]
                                                                                        );
                                                                                        if (confirm) {
                                                                                            return addTalentResourceMember(resource.id)
                                                                                        }
                                                                                    }} disabled={isUpdating}>
                                                                                    <span className="underline">
                                                                                        {resource.firstNamePlusInitial()}
                                                                                    </span>
                                                                                </button>
                                                                            ))}
                                                                        </span>
                                                                    ) : auth.canManageTalent() ? (
                                                                        <TalentManagerFillTeamMemberModal talentDiscipline={discipline}/>
                                                                    ) : (
                                                                        <span>
                                                                            No Matched Resources
                                                                        </span>
                                                                    )
                                                                )
                                                            ))}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </Collapse.Panel>
                                    )
                                })}
                            </Collapse>
                        </div>
                    )
                }) : (
                    <div
                        className="bg-white p-4 border-b border-bluegray min-h-[300px] flex flex-col items-center justify-center">
                        <Empty description="No project types added yet" image="/images/team.png"
                               imageStyle={{margin: "0 auto", height: 200, opacity: 0.4}}
                               className="img-grayscale">
                            {team.can_manage && (
                                <Link to="../project-types" className="btn btn-dark">
                                    <i className="fas fa-pen mr-2 text-white"/> Edit Project Types
                                </Link>
                            )}
                        </Empty>
                    </div>
                )}

                {team.can_manage && team.talent_service_groups?.length > 0 && (
                    <div className="flex gap-4">
                        <div>
                            <p className="text-xs mb-2">{servicesReadyForApproval?.length || 0} projects ready</p>
                            {team.can_manage && (
                                <button className="btn btn-primary"
                                        disabled={isUpdating || !servicesReadyForApproval?.length}
                                        onClick={() => requestApprovalForTeamServices(servicesReadyForApproval)}>

                                    <i className="fas fa-sign-in-alt mr-2 text-white"/> {auth.canManageTalent() ? 'Approve All' : 'Request Approval Now'}
                                </button>
                            )}
                        </div>

                        {!isManaging && !isSupplier && rolesMissingForServices?.length > 0 && (
                            <div>
                                <p className="text-xs mb-2">
                                    {rolesMissingForServices?.length} roles missing across all projects.
                                </p>
                                {team.can_manage && (
                                    <button className="btn btn-primary" disabled={isUpdating}
                                            onClick={() => requestHelpFillingDisciplines(rolesMissingForServices)}>
                                        <i className="fas fa-user-plus mr-2 text-white"/> Let Wripple Help Fill Missing
                                        Roles
                                    </button>
                                )}
                            </div>
                        )}

                    </div>
                )}
            </div>
        </div>
    );
}

export default TalentTeamManage;